/**
 * Clever OAuth callback route (/clever/callback).
 *
 * Clever redirects here with `?code=...` after the user authorizes the app.
 * We hand the code to completeCleverLogin (which exchanges it server-side for
 * a Firebase custom token and signs in), then route to /dashboard.
 *
 * Clever users are district-provisioned, so there is NO ToS/accept-terms
 * detour here (unlike the Google/Microsoft new-user path) — the district's
 * agreement covers consent.
 *
 * Credential-gated & UNTESTED against Clever: behaves correctly per Clever's
 * documented OAuth flow but has not run against a live Clever app.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeCleverLogin, consumeCleverState } from '../../services/rosteringService';
import { createComponentLogger } from '../../utils/logger';
import { getErrorMessage } from '../../utils/errorHandler';
import './Auth.css';

const logger = createComponentLogger('CleverCallback');

export const CleverCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  // Guard against React 18 StrictMode double-invoking the effect, which would
  // attempt to redeem the single-use OAuth code twice.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const oauthError = searchParams.get('error');
    const state = searchParams.get('state');

    if (oauthError) {
      logger.error('Clever returned an OAuth error:', oauthError);
      setError('Clever sign-in was cancelled or failed. Please try again.');
      return;
    }
    if (!code) {
      setError('Missing Clever authorization code. Please start the sign-in again.');
      return;
    }

    // S82: verify the one-time `state` this browser generated before redirecting
    // out. Without it, anyone holding a valid Clever authorization code for their
    // OWN account could hand a child a link to
    // `<origin>/clever/callback?code=<attacker code>`, and this page would redeem
    // it and silently sign the child into the attacker's account — where every
    // game the child then wrote could be read. Consumed here whether it matches
    // or not, so a code cannot be replayed against the same pending login.
    if (!consumeCleverState(state)) {
      logger.error('Clever callback arrived without a matching OAuth state');
      setError(
        'We could not verify that this sign-in started here. ' +
          'Please go back and press "Log in with Clever" again.'
      );
      return;
    }

    completeCleverLogin(code)
      .then(() => {
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        logger.error('Clever callback failed:', err);
        setError(getErrorMessage(err) || 'Could not finish signing in with Clever.');
      });
  }, [searchParams, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        {error ? (
          <>
            <h1 className="auth-title">Sign-in failed</h1>
            <div className="auth-error" role="alert">
              {error}
            </div>
            <button
              type="button"
              className="auth-button"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <h1 className="auth-title">Signing you in…</h1>
            <p className="auth-subtitle">Finishing your Clever sign-in. One moment.</p>
          </>
        )}
      </div>
    </div>
  );
};
